import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import throttle from 'lodash/throttle';
import {AttrsSchema, Component} from 'panel';

import {TooltipBulletStyle, TooltipItemType, TooltipRow, TooltipSection} from 'common/components/chart-tooltip/types';
import {abbreviateNumber, abbreviateNumber3SigFigs, abbreviateNumberNSigFigs, commaizeNumber} from 'common/util';
import * as colors from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {assertUnreachable} from 'common/util/typescript';
import {CountType as FunnelsCountType} from 'common/report/models/funnels/types';
import {formatPercent} from 'common/report/util/funnels/utils';
import {calculatePopoverPosition, Position} from 'common/report/util/funnels/positioning';
import {getMarginBetweenBarSegments} from 'common/report/util/funnels/width-calculation';

import {
  BarType,
  BarWithSize,
  ChartableStep,
  ClosePopoverEvent,
  ContextMenuStepEvent,
  CountType,
  HighlightedSteps,
  SelectBarEvent,
  SelectColumnEvent,
  SelectedBar,
  Step,
  UpdateStartStepEvent,
} from './types';
import {barHeight, chartableSteps, getLastStepValue, getMaxStepValue, getYInterval, minimumBarHeight} from './utils';
import template from './index.jade';
import css from './index.styl?inline';

const CHART_LABEL_HEIGHT = 90;
const CHART_BAR_HEIGHT = 260;
const MULTI_BAR_CHART_COUNT_HEIGHT = 50;
const SINGLE_BAR_CHART_COUNT_HEIGHT = 90;
const LABEL_HEIGHT = 36;
const MINIMUM_LABEL_BOTTOM_OFFSET = 8;

export const MINIMUM_BAR_WIDTH_TO_SHOW_COUNT_LABELS = 45;

export interface FunnelsChartSizing {
  topMargin: number;
  barRowHeight: number;
  labelRowHeight: number;
  yAxisContainerWidth: number;
}

export const DEFAULT_FUNNELS_CHART_SIZING: FunnelsChartSizing = {
  topMargin: 26,
  barRowHeight: 271,
  labelRowHeight: 70,
  yAxisContainerWidth: 52,
};

export interface FunnelsChartAttrs {
  colors: string[];
  count: CountType;
  'custom-chart-sizes'?: FunnelsChartSizing;
  'date-range-labels': string[];
  'disable-context-menu': boolean;
  'disable-drop-off-click': boolean;
  'disable-drop-off-viz': boolean;
  'disable-header-click': boolean;
  'disable-step-click': boolean;
  'disable-tooltip': boolean;
  'funnels-count-type': FunnelsCountType;
  'highlighted-steps': HighlightedSteps;
  'include-step-counts': boolean;
  loading: boolean;
  'column-left-margin': number;
  'column-width': number;
  'column-grid-gap': number;
  'last-column-padding': number;
  'no-grid': boolean;
  'selected-bar': SelectedBar;
  'selected-column': number | null;
  'show-conversion-rate': boolean;
  'start-step': number;
  'v2-design': boolean;
  'visible-steps': number;
  'show-scrollbar': boolean;
}

interface HoverTooltipState {
  stepIdx: number;
  barIdx: number;
  barType: BarType;
  isComparison: boolean;
  styles: Position;
}

interface ChartState {
  steps?: ChartableStep[];
  hoverTooltip?: HoverTooltipState;
}

export class FunnelsChart extends Component<ChartState, FunnelsChartAttrs> {
  private lastStepUserCount: number;
  private maxOverallStepValue: number;
  /** how many bars are being shown per column */
  private barsPerColumn: number;

  static get attrsSchema(): AttrsSchema<FunnelsChartAttrs> {
    return {
      colors: {type: `json`, default: []},
      count: {type: `string`, enum: [CountType.RELATIVE, CountType.ABSOLUTE], default: CountType.ABSOLUTE},
      'custom-chart-sizes': {type: `json`, tsType: `FunnelsChartSizing`},
      'date-range-labels': {type: `json`, default: [], description: `date ranges required for comparison queries`},
      'disable-context-menu': {type: `boolean`, description: `disable special right click actions`},
      'disable-drop-off-click': {type: `boolean`, description: `disable click on the drop-off bars`},
      'disable-drop-off-viz': {type: `boolean`, description: `don't show drop-off bars on each step`},
      'disable-header-click': {type: `boolean`, description: `disable clicking the header to select a step`},
      'disable-step-click': {type: `boolean`, description: `disable click on the conversion bars`},
      'disable-tooltip': {type: `boolean`, description: `don't show tooltip when there is popover`},
      'funnels-count-type': {type: `string`, enum: [`unique`, `general`, `session`]},
      'highlighted-steps': {type: `json`, description: `interface: HighlightedSteps`, default: {indices: []}},
      'include-step-counts': {type: `boolean`, description: `to add step counts under steps`},
      loading: {type: `boolean`, description: `if loading, show a spinner`},
      'column-left-margin': {
        type: `number`,
        description: `the space (in px) between the yAxis and the start of the steps`,
      },
      'column-width': {type: `number`, description: `the column width (in px) for each step. only used in v2 design`},
      'column-grid-gap': {type: `number`, description: `the gap between each step. (in px) only used in v2 design`},
      'last-column-padding': {
        type: `number`,
        default: 0,
        description: `padding added after last column. (in px) only used in v2 design`,
      },
      'no-grid': {
        type: `boolean`,
        description: `Do not render y-axis labels or grid lines. Only works with v2-design.`,
      },
      'selected-bar': {type: `json`, description: `interface: SelectedBar - index of selected bar type`, default: {}},
      'selected-column': {type: `number`, description: `the index of the selected column`, default: null},
      'show-conversion-rate': {type: `boolean`, description: `show an overall conversion rate on the chart`},
      'show-scrollbar': {
        type: `boolean`,
        description: `show the scrollbar underneath the chart (used in dashboards for discoverability)`,
      },
      'start-step': {type: `number`, description: `index of first step to be displayed`},
      'v2-design': {type: `boolean`, description: `whether to use the v2 design`},
      'visible-steps': {type: `number`, description: `number of steps to be displayed`},
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        hoverTooltip: null,
      },
      helpers: {
        BarType,
        CHART_BAR_HEIGHT,
        abbreviateNumber,
        abbreviateNumber3SigFigs,
        commaizeNumber,
        handleMouseMove: throttle(
          (stepIdx, barIdx, barType, isComparison, ev) => {
            const composedPath = ev.composedPath();
            const hoverTooltip: HoverTooltipState = {
              stepIdx,
              barIdx,
              barType,
              isComparison,
              styles: calculatePopoverPosition({
                barElement: ev.currentTarget,
                chartElement: composedPath.find((element) => element.classList?.contains(`columns-v2`)),
                viewerElement: this,
              }),
            };
            this.update({hoverTooltip});
          },
          50,
          {leading: true, trailing: false},
        ),
        handleMouseLeave: () => {
          // cancel any pending mousemoves, this ensures we don't get a "mousemove after mouseleave" which results in a stuck tooltip
          this.helpers.handleMouseMove.cancel();
          this.update({hoverTooltip: null});
        },
        shouldShowTooltip: () => {
          const {hoverTooltip} = this.state;
          return !this.attr(`disable-tooltip`) && hoverTooltip;
        },
        getUserConversionRate: () => formatPercent(this.lastStepUserCount / this.maxOverallStepValue),
        getUserCount: () => commaizeNumber(this.lastStepUserCount),
        getMarginBetweenBarSegments: () =>
          getMarginBetweenBarSegments(this.barsPerColumn, this.attr(`column-grid-gap`)),
        hasConversionToNext: (step) => {
          // conversion arrow is only shown if single funnel
          return step.data.length === 1 && isNumber(this.helpers.firstBarInStep(step).conversionToNext);
        },

        isUnselected: (stepIndex) => {
          const selectedColumn = this.attr(`selected-column`);
          if (isNumber(selectedColumn)) {
            return stepIndex !== selectedColumn;
          } else {
            return false;
          }
        },

        isSelected: (stepIndex: number, barIndex: number, stepType: BarType) => {
          const selectedBar: SelectedBar = this.attr(`selected-bar`);
          if (stepIndex !== selectedBar.index || stepType !== selectedBar.type) {
            return false;
          }

          let result = barIndex === selectedBar.barIdx;
          if (this.helpers.hasComparison() && !result) {
            // also highlight the comparison bar
            const comparisonBarIdx = barIndex % 2 === 1 ? barIndex - 1 : barIndex + 1;
            result = comparisonBarIdx === selectedBar.barIdx;
          }
          return result;
        },

        isHighlighted: (stepIndex: number, barIdx: number, stepType: BarType) => {
          const {indices, type} = this.getHighlightedSteps();
          return indices.some((i) => i.index === stepIndex && i.barIdx === barIdx) && stepType === type;
        },

        unselectBarAndColumn: () => {
          this.unselectBar();
          this.unselectColumn();
        },

        hasDropOff: (index, barIdx, step) => {
          if (index === 0) {
            return false;
          }

          const prevStep = this.overallSteps[index - 1].data[barIdx];
          return step.data[barIdx].value !== prevStep.value;
        },

        selectBar: (selectedStepIndex, selectedBarIndex, selectedBarType) => (ev) => {
          ev.stopPropagation();
          const {index, barIdx, type} = this.attr(`selected-bar`);
          if (selectedStepIndex === index && selectedBarIndex === barIdx && selectedBarType === type) {
            this.unselectBar();
          } else {
            this.selectBar(selectedStepIndex, selectedBarIndex, selectedBarType, ev);
          }
        },

        selectColumn: (idx) => {
          const selectedColumn = this.attr(`selected-column`);
          if (idx === selectedColumn) {
            this.unselectColumn();
          } else {
            this.selectColumn(idx);
          }
        },

        formatPercent,

        getSlopeColor: () => {
          return {
            startColor: `#ecf3f8`, // TODO: move to mixpanel-common stylus variable
            stopColor: `#ffffff`,
          };
        },

        getYAxisLabels: (steps: Step[]) => {
          if (!steps || steps.length === 0 || steps.every((step) => step.data.length === 0)) {
            // @ts-ignore
            return;
          }

          if (this.attr(`count`) === CountType.RELATIVE) {
            return [
              {text: `0%`, y: 0},
              {text: `25%`, y: 0.25},
              {text: `50%`, y: 0.5},
              {text: `75%`, y: 0.75},
              ...(this.attr(`v2-design`) ? [] : [{text: `100%`, y: 1}]),
            ];
          } else {
            const yInterval = getYInterval(this.maxOverallStepValue);
            // + 1 for the zero label
            const yLabelCount = Math.min(Math.floor(this.maxOverallStepValue / yInterval), 6) + 1;
            return Array(yLabelCount)
              .fill(null)
              .map((_, idx) => {
                const value = idx * yInterval;
                return {
                  text: abbreviateNumber(value),
                  y: value / this.maxOverallStepValue, // ratio from 0 - 1
                };
              });
          }
        },

        shouldShowBarCountLabels: () => {
          let columnWidth = this.attr(`column-width`);

          if (this.hasMultipleSeries) {
            const gapBetweenBars = getMarginBetweenBarSegments(this.barsPerColumn, this.attr(`column-grid-gap`));

            // Gap spacing between all of the breakdown/segments
            const gapSpacing = gapBetweenBars * (this.barsPerColumn - 1);

            // the "true" bar width is the space allocated for the column minus the spacing evenly divided into bars
            columnWidth = (columnWidth - gapSpacing) / this.barsPerColumn;
          }

          // Should only show if the bar size is going to be wide enough to show the label
          return columnWidth > MINIMUM_BAR_WIDTH_TO_SHOW_COUNT_LABELS;
        },

        getBarLabel: (bar: BarWithSize): {conversionPercent: string; conversion: string} => {
          let conversionPercentString;
          const conversionPercent = bar.conversionRate * 100;

          if (conversionPercent >= 99.5) {
            // abbrv doesn't handle numbers >= 100 (does 1e+02), and abbreviateNumber will round 99.5 => 1e+02
            conversionPercentString = `100`;
          } else if (conversionPercent < 1) {
            // 0.XX takes 4 characters which is too many so we lose precision in this case
            conversionPercentString = abbreviateNumberNSigFigs(conversionPercent, 1);
          } else {
            conversionPercentString = abbreviateNumberNSigFigs(conversionPercent, 2);
          }

          return {
            conversionPercent: `${conversionPercentString}%`,
            conversion: abbreviateNumber3SigFigs(bar.value),
          };
        },

        /**
         * Computes the offset to place the count label relative to the bar
         * Note: we want the labels to be offset halfway so they live half on the bar, and half over the top,
         * but also ensure we have at least MINIMUM_LABEL_BOTTOM_OFFSET space between the bottom of the bar
         * and the label so the user can still see the bar
         */
        getBarLabelOffset: (bar: BarWithSize): {bottom: string} => {
          // convertedBarHeight is in range 0-100, so transform to a 0.XX and multiply by the actual row height to get height in px
          const barHeight = (bar.convertedBarHeight / 100) * this.getChartSizing().barRowHeight;

          // Labels are centered in the middle, so we adjust to be fixed bottom if they would have less than
          // a minimum space between the bottom of the bar and the label
          if (barHeight - LABEL_HEIGHT / 2 < MINIMUM_LABEL_BOTTOM_OFFSET) {
            return {bottom: `${MINIMUM_LABEL_BOTTOM_OFFSET}px`};
          }

          // offset labels by half the height so they are centered in the bar halfway
          return {bottom: `calc(100% - ${LABEL_HEIGHT / 2}px)`};
        },

        getVariableColumnStyles: () => {
          const {steps} = this.state;
          if (this.attr(`v2-design`)) {
            return {
              'grid-auto-columns': `${this.attr(`column-width`)}px`,
              'grid-column-gap': `${this.attr(`column-grid-gap`)}px`,
              'padding-right': `${this.attr(`last-column-padding`)}px`,
            };
          } else {
            const columnGap = this.hasMultipleSeries ? 5 : 0;

            const visibleSteps = this.attr(`visible-steps`);
            const spaceWithoutGaps = 100 - columnGap * (visibleSteps - 1);
            const repeatPercent = Math.ceil(spaceWithoutGaps / visibleSteps);
            const gridTemplateColumns =
              steps.length >= visibleSteps
                ? `repeat(${steps.length}, ${repeatPercent}%)`
                : `repeat(auto-fit, minmax(200px, 1fr))`;
            return {
              ...(this.helpers.hasComparison() ? {} : {'grid-template-columns': gridTemplateColumns}),
              'grid-column-gap': `${columnGap}%`,
            };
          }
        },

        getTemplateRowStyle: () => {
          if (this.attr(`v2-design`)) {
            const offsetDown = this.helpers.includeStepCounts() ? 0 : 20;
            const {topMargin, barRowHeight, labelRowHeight} = this.getChartSizing();

            return `${topMargin + offsetDown}px ${barRowHeight}px ${labelRowHeight - offsetDown}px`;
          } else {
            const countHeight = this.hasMultipleSeries ? MULTI_BAR_CHART_COUNT_HEIGHT : SINGLE_BAR_CHART_COUNT_HEIGHT;
            return `${CHART_LABEL_HEIGHT}px ${countHeight}px ${CHART_BAR_HEIGHT}px`;
          }
        },

        getChartHeightStyle: () => {
          if (this.attr(`v2-design`)) {
            const {topMargin, barRowHeight, labelRowHeight} = this.getChartSizing();

            return `${topMargin + barRowHeight + labelRowHeight}px`;
          } else {
            const countHeight = this.hasMultipleSeries ? MULTI_BAR_CHART_COUNT_HEIGHT : SINGLE_BAR_CHART_COUNT_HEIGHT;
            return `${CHART_LABEL_HEIGHT + countHeight + CHART_BAR_HEIGHT}px`;
          }
        },

        getYAxisContainerWidth: () => {
          return `${this.getChartSizing().yAxisContainerWidth}px`;
        },

        getColumnsLeftMargin: () => {
          return `${this.attr(`column-left-margin`)}px`;
        },

        handleScroll: debounce(() => {
          this.updateIndices(this.getFirstVisibleStepIndex());
          if (this.attr(`selected-bar`)) {
            this.closePopover();
          }
        }, 10),

        trackScroll: throttle((ev) => {
          const startIndex = this.getFirstVisibleStepIndex();
          this.el.dispatchEvent(
            new CustomEvent(`track`, {
              bubbles: true,
              composed: true,
              detail: {
                eventName: `[Funnels] Scroll chart steps`,
                properties: {
                  startIndex,
                  direction: ev.deltaX > 0 ? `right` : `left`,
                },
              },
            }),
          );
        }, 500),

        indexIsVisible: (idx) => {
          const startStep = this.attr(`start-step`);
          return idx >= startStep && idx < startStep + this.attr(`visible-steps`);
        },

        conversionArrowStyles: (firstBarInStep, stepIdx) => {
          const barIdx = 0;
          let startY = barHeight(
            firstBarInStep.value,
            stepIdx,
            barIdx,
            this.overallSteps,
            this.attr(`count`),
            this.maxOverallStepValue,
          );
          startY = Math.max(minimumBarHeight, startY);
          let endY = barHeight(
            firstBarInStep.value * firstBarInStep.conversionToNext,
            stepIdx,
            barIdx,
            this.overallSteps,
            this.attr(`count`),
            this.maxOverallStepValue,
          );
          endY = Math.max(minimumBarHeight, endY);
          const bottom = Math.max(startY - (startY - endY) / 2, 8);

          return {bottom: `${bottom}%`};
        },

        contextMenu: (ev, step) => {
          if (this.attr(`disable-context-menu`)) {
            return;
          }

          ev.preventDefault();
          ev.stopPropagation();
          this.dispatchEvent(
            new CustomEvent(`contextMenuStep`, {
              detail: {
                step,
                mouseEvent: ev,
              },
              bubbles: true,
              composed: true,
            }) as ContextMenuStepEvent,
          );
        },

        getColor: (barIdx: number, type: BarType, hasComparison = false) => {
          if (hasComparison) {
            // comparison bar colors are paired
            barIdx = Math.floor(barIdx / 2);
          }
          const overrideColors = this.attr(`colors`);
          if (overrideColors && overrideColors.length > 0) {
            return overrideColors[barIdx];
          } else {
            if (type === BarType.CONVERTED) {
              if (this.hasMultipleSeries) {
                return colors.getChartSegmentColorCombo(barIdx).primary;
              } else {
                return colors.getChartSegmentColorCombo(0).primary;
              }
            } else {
              return colors.getChartSegmentColorCombo(barIdx).primary;
            }
          }
        },
        // TODO opslead
        // eslint-disable-next-line sonarjs/cognitive-complexity
        getBarStyle: ({bar, barIdx, barType}: {bar: BarWithSize; barIdx: number; barType: BarType}) => {
          const hasComparison = this.helpers.hasComparison();
          const isComparisonBar = hasComparison && barIdx % 2 === 0;
          const color = bar.color || this.helpers.getColor(barIdx, barType, hasComparison);
          const props: Partial<{
            'background-image': string;
            'background-size': string;
            background: string;
            height: string;
            bottom: string;
          }> = {};

          if (this.attr(`v2-design`)) {
            if (isComparisonBar) {
              if (barType === BarType.DROP_OFF) {
                props[`background-image`] = this.dropOffGradientStyle(color);
              } else {
                props[`background-image`] = this.stripedBackgroundStyle(color);
                props[`background-size`] = `4px 4px`;
              }
            } else {
              props.background = barType === BarType.DROP_OFF ? this.dropOffGradientStyle(color) : color;
            }
          } else {
            if (isComparisonBar) {
              props[`background-image`] = this.stripedBackgroundStyle(color);
              props[`background-size`] = `4px 4px`;
            } else {
              props.background = color;
            }
          }

          if (barType === BarType.DROP_OFF) {
            const dividerSize = 1;
            props.height = `calc(${bar.dropOffBarHeight}% - ${dividerSize}px)`;
            props.bottom = `calc(${bar.convertedBarHeight}% + ${dividerSize}px)`;
          } else if (barType === BarType.CONVERTED) {
            props.height = `${bar.convertedBarHeight}%`;
          } else {
            return assertUnreachable(barType);
          }

          return props;
        },
        getStripedMaskStyle: (barIdx) => {
          const hasComparison = this.helpers.hasComparison();
          const isComparisonBar = hasComparison && barIdx % 2 === 0;
          if (isComparisonBar) {
            return {
              'background-image': `repeating-linear-gradient(-45deg, rgba(0, 0, 0, 0), rgba(0, 0, 0,0) 25%, rgb(255, 255, 255) 25%, rgb(255, 255, 255) 50%, rgba(0, 0, 0,0) 50%)`,
              'background-size': `4px 4px`,
            };
          } else {
            return {display: `none`};
          }
        },
        countHeader: (step) => {
          return commaizeNumber(this.helpers.firstBarInStep(step)?.value);
        },
        getConversionRate: (step) => {
          return formatPercent(this.helpers.firstBarInStep(step)?.conversionRate);
        },
        firstBarInStep: (step: ChartableStep) => {
          return step.data[0];
        },
        getDropOffCount: ({stepIdx, barIdx, bar}: {stepIdx: number; barIdx: number; bar: BarWithSize}) => {
          let dropOffCount = null;
          if (stepIdx > 0) {
            const lastStepCount = this.state.steps[stepIdx - 1].data[barIdx].value;
            dropOffCount = commaizeNumber(lastStepCount - bar.value);
          }
          return dropOffCount;
        },

        getTooltipAttrs: () => {
          const {stepIdx, barIdx, isComparison, barType} = this.state.hoverTooltip;
          const bar = this.state.steps[stepIdx].data[barIdx];
          const color = bar.color || this.helpers.getColor(barIdx, BarType.CONVERTED, isComparison);

          const countType = this.attr(`funnels-count-type`) === `unique` ? `uniques` : `total times`;

          const generateDataRows = ({
            stepIdx,
            barIdx,
            bar,
          }: {
            stepIdx: number;
            barIdx: number;
            bar: BarWithSize;
          }): TooltipRow[] => {
            const conversionPercentage = abbreviateNumber(bar.conversionRate * 100);
            const conversionCount = commaizeNumber(bar.value);
            const dropOffPercentage = abbreviateNumber((1 - bar.conversionRate) * 100);
            const dropOffCount = this.helpers.getDropOffCount({stepIdx, barIdx, bar});

            const percentage = barType === BarType.DROP_OFF ? dropOffPercentage : conversionPercentage;
            const count = barType === BarType.DROP_OFF ? dropOffCount : conversionCount;
            return [
              {
                items: [
                  {text: `${percentage}%`, type: TooltipItemType.Data},
                  {text: barType, type: TooltipItemType.Text},
                ],
              },
              {
                items: [
                  {text: count, type: TooltipItemType.Data},
                  {text: countType, type: TooltipItemType.Text},
                ],
              },
            ];
          };

          let sections: TooltipSection[];
          const titleSection = {rows: [{items: [{text: bar.title, type: TooltipItemType.Strong}]}]};

          if (isComparison) {
            const currentBarIdx = barIdx % 2 === 1 ? barIdx : barIdx + 1;
            const pastBarIdx = barIdx % 2 === 1 ? barIdx - 1 : barIdx;
            const currentBar = this.state.steps[stepIdx].data[currentBarIdx];
            const pastBar = this.state.steps[stepIdx].data[pastBarIdx];
            const generateDateRow = (label) => ({items: [{text: label, type: TooltipItemType.Strong}]});
            sections = [
              titleSection,
              {
                bulletColor: color,
                bulletStyle: TooltipBulletStyle.Solid,
                rows: [
                  generateDateRow(this.attr(`date-range-labels`)[0]),
                  ...generateDataRows({stepIdx, barIdx: currentBarIdx, bar: currentBar}),
                ],
              },
              {},
              {
                bulletColor: color,
                bulletStyle: TooltipBulletStyle.Dash,
                rows: [
                  generateDateRow(this.attr(`date-range-labels`)[1]),
                  ...generateDataRows({stepIdx, barIdx: pastBarIdx, bar: pastBar}),
                ],
              },
            ];
          } else {
            sections = [titleSection, {rows: generateDataRows({stepIdx, barIdx, bar})}];
          }

          return {sections: JSON.stringify(sections), ...(isComparison ? {} : {'title-color': color})};
        },
        hasComparison: () => this.attr(`date-range-labels`).length > 1,

        includeStepCounts: () => this.attr(`include-step-counts`) && !this.hasMultipleSeries,
      },
    };
  }

  getScrollBox(): HTMLElement {
    const scrollBoxSelector = this.attr(`v2-design`) ? `.columns-v2-wrapper` : `.columns`;
    return this.el.querySelector(scrollBoxSelector);
  }

  /**
   * When a user scrolls natively, we cannot detect which item is now the startStep,
   * so the workaround is to calculate the width of one of the steps.
   */
  private getFirstVisibleStepIndex() {
    const scrollBox = this.getScrollBox();
    // setting this as a constant is brittle but avoids getComputedStyle
    const scrollPaddingLeft = 15;
    const stepWidth = this.getStepWidth(scrollBox);
    return Math.round((scrollBox.scrollLeft + scrollPaddingLeft) / stepWidth);
  }

  private getStepWidth(columns: HTMLElement) {
    if (this.attr(`v2-design`) || !columns) {
      return this.attr(`column-width`) + this.attr(`column-grid-gap`);
    } else {
      const columnGap = (parseFloat(columns.style.columnGap) / 100.0) * columns.clientWidth;
      const column = columns.children[0] as HTMLElement;
      return column.offsetWidth + columnGap;
    }
  }

  private stripedBackgroundStyle(color) {
    return `repeating-linear-gradient(-45deg, ${color}, ${color} 25%, #FFF 25%, #FFF 50%, ${color} 50%)`;
  }

  private dropOffGradientStyle(color) {
    const solidColor = colors.rgbaStringFromHexString(color, 1);
    const translucentColor = colors.rgbaStringFromHexString(color, 0.05);
    return `linear-gradient(to bottom, ${solidColor} 0%, ${translucentColor} 100%)`;
  }

  private unselectBar() {
    const {type} = this.attr(`selected-bar`);
    this.selectBar(null, null, type);
  }

  private selectBar(index, barIdx, type, mouseEvent?) {
    this.unselectColumn();
    this.el.dispatchEvent(
      new CustomEvent(`selectBar`, {
        detail: {
          index,
          barIdx,
          type,
          mouseEvent,
        },
        bubbles: true,
        composed: true,
      }) as SelectBarEvent,
    );
  }

  private unselectColumn() {
    if (isNumber(this.attr(`selected-column`))) {
      this.selectColumn(null);
    }
  }

  private selectColumn(index) {
    this.el.dispatchEvent(
      new CustomEvent(`selectColumn`, {
        detail: {
          index,
        },
        bubbles: true,
        composed: true,
      }) as SelectColumnEvent,
    );
    if (isNumber(index) && !this.helpers.indexIsVisible(index)) {
      this.updateIndices(this.attr(`start-step`) + 1);
    }
  }

  private closePopover() {
    this.el.dispatchEvent(
      new CustomEvent(`closePopover`, {
        bubbles: true,
        composed: true,
      }) as ClosePopoverEvent,
    );
  }

  private updateIndices(startStep) {
    this.el.dispatchEvent(
      new CustomEvent(`updateStartStep`, {
        detail: {
          index: startStep,
        },
        bubbles: true,
        composed: true,
      }) as UpdateStartStepEvent,
    );
  }

  // N.B. this includes all segments (not just the "overall") if there is a breakdown
  set overallSteps(steps: Step[]) {
    if (Array.isArray(steps)) {
      this.maxOverallStepValue = getMaxStepValue(steps);

      const _steps = chartableSteps(steps, this.attr(`count`), this.maxOverallStepValue);
      this.barsPerColumn = get(_steps, `[0].data.length`, 0);
      this.lastStepUserCount = getLastStepValue(_steps);
      this.update({steps: _steps});
    } else {
      this.update({steps: null});
    }
  }
  get overallSteps() {
    return this.state.steps;
  }
  get hasMultipleSeries() {
    return this.barsPerColumn > 1;
  }

  private getHighlightedSteps(): HighlightedSteps {
    return this.attr(`highlighted-steps`);
  }

  getChartSizing(): FunnelsChartSizing {
    if (this.attr(`custom-chart-sizes`)) {
      return this.attr(`custom-chart-sizes`);
    }

    return DEFAULT_FUNNELS_CHART_SIZING;
  }

  public connectedCallback() {
    super.connectedCallback();

    this.dispatchEvent(
      new CustomEvent(`scrollBoxMount`, {
        detail: {vnode: this},
        bubbles: true,
        composed: true,
      }),
    );
  }
}

export const FunnelsChartEl = defineAndMakeJsxFactory(`mp-funnels-chart`, FunnelsChart);
